import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectAccessToken } from '../../containers/App/selectors';

import {
  DEFAULT_ULTRA_HEAD_COLORS,
  DEFAULT_ULTRA_PREFS,
  type UltraHeadColors,
  type UltraPrefs,
  type UltraStatOverride,
  type UltraStyleSlice,
  normalizeUltraPrefs,
  readUltraPrefs,
  resolveUltraItemBackground,
  ULTRA_PREFS_KEY,
  writeUltraPrefs,
} from '../utils/ultraPrefs';
import {
  allHudsExportFilename,
  applyUltraHud,
  buildAllUltraHudsExport,
  buildUltraHudExport,
  deleteUltraHud,
  downloadJsonFile,
  hudExportFilename,
  mergeImportedUltraHuds,
  parseUltraHudImport,
  pullUltraStorageFromServer,
  pushUltraStorageToServer,
  readUltraHuds,
  saveUltraHud,
  writeUltraHuds,
  type UltraHud,
} from '../utils/ultraHuds';

type Props = {
  prefs: UltraPrefs;
  onChange: (next: UltraPrefs) => void;
  onApplyActivePrefs?: (next: UltraPrefs) => void;
  editRows: boolean;
  onEditRowsChange: (value: boolean) => void;
  selectedSlotIndex?: number | null;
  selectedStatKey?: string | null;
  selectedStatLabel?: string | null;
};

function applyPatch<T extends Record<string, unknown>>(base: T | undefined, patch: Partial<T>): T {
  const next = { ...(base ?? {}) } as T;
  (Object.keys(patch) as Array<keyof T>).forEach((field) => {
    const value = patch[field];
    if (value === undefined) {
      delete next[field];
    } else {
      next[field] = value as T[keyof T];
    }
  });
  return next;
}

function numberField(
  label: string,
  value: number,
  onChange: (value: number) => void,
  min = 6,
  max = 32,
) {
  return (
    <label className="portable-ultra-panel__field">
      {label}
      <input
        type="number"
        className="portable-ultra-field portable-ultra-field--num"
        min={min}
        max={max}
        value={value}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) {
            onChange(Math.min(max, Math.max(min, next)));
          }
        }}
      />
    </label>
  );
}

function optionalNumberField(
  label: string,
  value: number | undefined,
  placeholder: number,
  onChange: (value: number | undefined) => void,
  min = 6,
  max = 32,
) {
  return (
    <label className="portable-ultra-panel__field">
      {label}
      <input
        type="number"
        className="portable-ultra-field portable-ultra-field--num"
        min={min}
        max={max}
        placeholder={String(placeholder)}
        value={value ?? ''}
        onChange={(event) => {
          if (event.target.value === '') {
            onChange(undefined);
            return;
          }

          const next = Number(event.target.value);
          if (Number.isFinite(next)) {
            onChange(Math.min(max, Math.max(min, next)));
          }
        }}
      />
    </label>
  );
}

function colorField(label: string, value: string, onChange: (value: string) => void) {
  return (
    <label className="portable-ultra-panel__field">
      {label}
      <input
        type="color"
        className="portable-ultra-field portable-ultra-field--color"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function optionalColorField(
  label: string,
  value: string | undefined,
  placeholder: string,
  onChange: (value: string | undefined) => void,
) {
  const display = value ?? placeholder;
  return (
    <label className="portable-ultra-panel__field">
      {label}
      <div className="portable-ultra-bg-field">
        <input
          type="color"
          className="portable-ultra-field portable-ultra-field--color"
          value={display.startsWith('#') ? display : '#1a1a1a'}
          onChange={(event) => onChange(event.target.value)}
        />
        {value ? (
          <button
            type="button"
            className="portable-ultra-chip portable-ultra-chip--tiny"
            onClick={() => onChange(undefined)}
          >
            авто
          </button>
        ) : null}
      </div>
    </label>
  );
}

function backgroundField(
  value: string | undefined,
  resolvedFallback: string,
  onChange: (value: string | undefined) => void,
) {
  const display = value ?? resolvedFallback;
  return (
    <label className="portable-ultra-panel__field portable-ultra-panel__field--wide">
      Фон
      <div className="portable-ultra-bg-field">
        <input
          type="color"
          className="portable-ultra-field portable-ultra-field--color"
          value={display.startsWith('#') ? display : '#1a1a1a'}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="portable-ultra-chip portable-ultra-chip--tiny"
          onClick={() => onChange(undefined)}
        >
          авто
        </button>
      </div>
    </label>
  );
}

const V2UltraStylePanel: React.FC<Props> = ({
  prefs,
  onChange,
  onApplyActivePrefs,
  editRows,
  onEditRowsChange,
  selectedSlotIndex = null,
  selectedStatKey = null,
  selectedStatLabel = null,
}) => {
  const accessToken = useSelector(selectAccessToken);
  const [columnTab, setColumnTab] = React.useState(0);
  const [hudName, setHudName] = React.useState('');
  const [huds, setHuds] = React.useState<UltraHud[]>(() => readUltraHuds());
  const [hudMessage, setHudMessage] = React.useState<string | null>(null);
  const importInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (columnTab >= prefs.columns) {
      setColumnTab(0);
    }
  }, [columnTab, prefs.columns]);

  const showHudMessage = (message: string) => {
    setHudMessage(message);
    window.setTimeout(() => setHudMessage(null), 2600);
  };

  const persistHuds = React.useCallback(
    async (nextHuds: UltraHud[], activePrefs = prefs, message?: string) => {
      writeUltraHuds(nextHuds);
      setHuds(nextHuds);

      if (!accessToken) {
        if (message) {
          showHudMessage(message);
        }
        return;
      }

      try {
        await pushUltraStorageToServer(accessToken, nextHuds, activePrefs);
        if (message) {
          showHudMessage(message);
        }
      } catch {
        showHudMessage(
          message ? `${message} (локальная копия)` : 'Сервер недоступен, сохранено локально',
        );
      }
    },
    [accessToken, prefs],
  );

  const applyActivePrefsRef = React.useRef(onApplyActivePrefs);
  applyActivePrefsRef.current = onApplyActivePrefs;

  React.useEffect(() => {
    let cancelled = false;

    async function loadStorage() {
      const localPrefs = readUltraPrefs();
      const hasLocalPrefs = localStorage.getItem(ULTRA_PREFS_KEY) != null;

      if (!accessToken) {
        setHuds(readUltraHuds());
        return;
      }

      try {
        const remote = await pullUltraStorageFromServer(accessToken);
        if (cancelled) {
          return;
        }

        const localHuds = readUltraHuds();
        if (remote.huds.length > 0) {
          setHuds(remote.huds);
        } else if (localHuds.length > 0) {
          await pushUltraStorageToServer(accessToken, localHuds, localPrefs);
          if (!cancelled) {
            setHuds(localHuds);
          }
        }

        if (remote.activePrefs && !hasLocalPrefs) {
          applyActivePrefsRef.current?.(
            normalizeUltraPrefs(remote.activePrefs as Partial<UltraPrefs>),
          );
        }
      } catch {
        if (!cancelled) {
          setHuds(readUltraHuds());
        }
      }
    }

    void loadStorage();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const updatePrefs = (patch: Partial<UltraPrefs>) => {
    const next = normalizeUltraPrefs({ ...prefs, ...patch });
    onChange(next);
    writeUltraPrefs(next);
  };

  const updateHeadColors = (patch: Partial<UltraHeadColors>) => {
    updatePrefs({
      headColors: {
        ...prefs.headColors,
        ...patch,
      },
    });
  };

  const updateColumn = (index: number, patch: Partial<UltraStyleSlice>) => {
    const columnOverrides = [...prefs.columnOverrides];
    while (columnOverrides.length < prefs.columns) {
      columnOverrides.push({});
    }

    columnOverrides[index] = applyPatch(columnOverrides[index], patch);
    updatePrefs({ columnOverrides });
  };

  const clearColumn = (index: number) => {
    const columnOverrides = [...prefs.columnOverrides];
    while (columnOverrides.length < prefs.columns) {
      columnOverrides.push({});
    }

    columnOverrides[index] = {};
    updatePrefs({ columnOverrides });
  };

  const updateStat = (key: string, patch: Partial<UltraStatOverride>) => {
    const statOverrides = { ...prefs.statOverrides };
    statOverrides[key] = applyPatch(statOverrides[key], patch);
    updatePrefs({ statOverrides });
  };

  const clearStat = (key: string) => {
    const statOverrides = { ...prefs.statOverrides };
    delete statOverrides[key];
    updatePrefs({ statOverrides });
  };

  const handleSaveHud = () => {
    const trimmedName = hudName.trim();
    if (!trimmedName) {
      showHudMessage('Введите название HUD');
      return;
    }

    const next = saveUltraHud(trimmedName, prefs, huds);
    setHudName('');
    void persistHuds(next, prefs, `HUD «${trimmedName}» сохранён`);
  };

  const handleLoadHud = (hud: UltraHud) => {
    const nextPrefs = normalizeUltraPrefs(applyUltraHud(hud));
    onChange(nextPrefs);
    writeUltraPrefs(nextPrefs);
    void persistHuds(huds, nextPrefs, `Загружен HUD «${hud.name}»`);
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    try {
      const parsed = parseUltraHudImport(JSON.parse(await file.text()));
      if (!parsed.length) {
        showHudMessage('Не удалось прочитать файл HUD');
        return;
      }

      const next = mergeImportedUltraHuds(parsed, huds);
      void persistHuds(next, prefs, `Импортировано HUD: ${parsed.length}`);
    } catch {
      showHudMessage('Ошибка импорта JSON');
    }
  };

  const activeColumn = prefs.columnOverrides[columnTab] ?? {};
  const activeStat = selectedStatKey ? (prefs.statOverrides[selectedStatKey] ?? {}) : null;
  const resolvedCellBackground =
    selectedStatKey && selectedSlotIndex != null
      ? resolveUltraItemBackground(prefs, selectedSlotIndex, selectedStatKey)
      : prefs.itemBackground;

  return (
    <section className="portable-ultra-panel" aria-label="Настройки Ultra">
      <div className="portable-ultra-huds">
        <p className="portable-ultra-huds__title">Шаблоны HUD</p>
        <div className="portable-ultra-huds__save">
          <input
            type="text"
            className="portable-ultra-field portable-ultra-field--text"
            value={hudName}
            placeholder="Название HUD"
            onChange={(event) => setHudName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSaveHud();
              }
            }}
          />
          <button type="button" className="portable-ultra-chip" onClick={handleSaveHud}>
            Сохранить HUD
          </button>
        </div>
        {hudMessage ? <p className="portable-ultra-huds__message">{hudMessage}</p> : null}
        {huds.length > 0 ? (
          <ul className="portable-ultra-huds__list">
            {huds.map((hud) => (
              <li className="portable-ultra-huds__item" key={hud.id}>
                <span className="portable-ultra-huds__name" title={hud.name}>
                  {hud.name}
                </span>
                <div className="portable-ultra-huds__actions">
                  <button
                    type="button"
                    className="portable-ultra-chip"
                    onClick={() => handleLoadHud(hud)}
                  >
                    Загрузить
                  </button>
                  <button
                    type="button"
                    className="portable-ultra-chip"
                    onClick={() =>
                      downloadJsonFile(hudExportFilename(hud.name), buildUltraHudExport(hud))
                    }
                  >
                    Экспорт
                  </button>
                  <button
                    type="button"
                    className="portable-ultra-chip portable-ultra-chip--danger"
                    onClick={() => void persistHuds(deleteUltraHud(hud.id, huds), prefs)}
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="portable-ultra-huds__empty">Пока нет сохранённых HUD</p>
        )}
        <div className="portable-ultra-huds__footer">
          <button
            type="button"
            className="portable-ultra-chip"
            disabled={huds.length === 0}
            onClick={() => downloadJsonFile(allHudsExportFilename(), buildAllUltraHudsExport(huds))}
          >
            Экспорт всех
          </button>
          <button
            type="button"
            className="portable-ultra-chip"
            onClick={() => importInputRef.current?.click()}
          >
            Импорт
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            className="portable-ultra-huds__file"
            onChange={handleImportFile}
          />
        </div>
      </div>

      <div className="portable-ultra-panel__row">
        <span className="portable-ultra-panel__label">Колонок</span>
        <div className="portable-ultra-panel__chips">
          {[2, 3, 4, 5].map((count) => (
            <button
              key={count}
              type="button"
              className={classNames(
                'portable-ultra-chip',
                prefs.columns === count && 'portable-ultra-chip--active',
              )}
              onClick={() => updatePrefs({ columns: count })}
            >
              {count}
            </button>
          ))}
        </div>
      </div>

      <div className="portable-ultra-row-editor">
        <p className="portable-ultra-row-editor__title">Шапка</p>
        <div className="portable-ultra-panel__grid">
          {colorField('Ник', prefs.headColors.playerNameColor, (value) =>
            updateHeadColors({ playerNameColor: value }),
          )}
          {colorField('Статы', prefs.headColors.valueColor, (value) =>
            updateHeadColors({ valueColor: value }),
          )}
          {colorField('Подпись h', prefs.headColors.suffixColor, (value) =>
            updateHeadColors({ suffixColor: value }),
          )}
          {colorField('Плюс W', prefs.headColors.positiveColor, (value) =>
            updateHeadColors({ positiveColor: value }),
          )}
          {colorField('Минус W', prefs.headColors.negativeColor, (value) =>
            updateHeadColors({ negativeColor: value }),
          )}
        </div>
        <button
          type="button"
          className="portable-ultra-chip"
          onClick={() => updateHeadColors({ ...DEFAULT_ULTRA_HEAD_COLORS })}
        >
          Сброс шапки
        </button>
      </div>

      <div className="portable-ultra-row-editor">
        <p className="portable-ultra-row-editor__title">Сетка статов</p>
        <div className="portable-ultra-panel__grid">
          {numberField('Метка px', prefs.labelFontSize, (value) =>
            updatePrefs({ labelFontSize: value }),
          )}
          {numberField('Знач. px', prefs.valueFontSize, (value) =>
            updatePrefs({ valueFontSize: value }),
          )}
          {colorField('Цвет метки', prefs.labelColor, (value) =>
            updatePrefs({ labelColor: value }),
          )}
          {colorField('Цвет знач.', prefs.valueColor, (value) =>
            updatePrefs({ valueColor: value }),
          )}
          {numberField('Высота', prefs.rowMinHeight, (value) =>
            updatePrefs({ rowMinHeight: value }),
          )}
          <label className="portable-ultra-panel__field portable-ultra-panel__field--wide">
            Фон ячеек
            <div className="portable-ultra-bg-field">
              <input
                type="color"
                className="portable-ultra-field portable-ultra-field--color"
                value={prefs.itemBackground.startsWith('#') ? prefs.itemBackground : '#141414'}
                onChange={(event) => updatePrefs({ itemBackground: event.target.value })}
              />
              <button
                type="button"
                className="portable-ultra-chip portable-ultra-chip--tiny"
                onClick={() => updatePrefs({ itemBackground: DEFAULT_ULTRA_PREFS.itemBackground })}
              >
                прозр.
              </button>
            </div>
          </label>
        </div>
      </div>

      <div className="portable-ultra-row-editor">
        <div className="portable-ultra-panel__row">
          <p className="portable-ultra-row-editor__title">Колонка</p>
          <div className="portable-ultra-panel__chips">
            {Array.from({ length: prefs.columns }, (_, index) => (
              <button
                key={index}
                type="button"
                className={classNames(
                  'portable-ultra-chip',
                  columnTab === index && 'portable-ultra-chip--active',
                )}
                onClick={() => setColumnTab(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
        <div className="portable-ultra-panel__grid">
          {optionalNumberField(
            'Метка px',
            activeColumn.labelFontSize,
            prefs.labelFontSize,
            (value) => updateColumn(columnTab, { labelFontSize: value }),
          )}
          {optionalNumberField(
            'Знач. px',
            activeColumn.valueFontSize,
            prefs.valueFontSize,
            (value) => updateColumn(columnTab, { valueFontSize: value }),
          )}
          {optionalColorField('Цвет метки', activeColumn.labelColor, prefs.labelColor, (value) =>
            updateColumn(columnTab, { labelColor: value }),
          )}
          {optionalColorField('Цвет знач.', activeColumn.valueColor, prefs.valueColor, (value) =>
            updateColumn(columnTab, { valueColor: value }),
          )}
          {backgroundField(activeColumn.background, prefs.itemBackground, (background) =>
            updateColumn(columnTab, { background }),
          )}
        </div>
        <button
          type="button"
          className="portable-ultra-chip"
          onClick={() => clearColumn(columnTab)}
        >
          Сброс колонки
        </button>
      </div>

      {editRows && selectedStatKey && activeStat ? (
        <div className="portable-ultra-row-editor">
          <p className="portable-ultra-row-editor__title">
            Ячейка {selectedSlotIndex != null ? selectedSlotIndex + 1 : ''}:{' '}
            {selectedStatLabel ?? selectedStatKey}
          </p>
          <div className="portable-ultra-panel__grid">
            <label className="portable-ultra-panel__field portable-ultra-panel__field--wide">
              Подпись
              <input
                type="text"
                className="portable-ultra-field portable-ultra-field--text"
                placeholder={selectedStatLabel ?? selectedStatKey}
                value={activeStat.customLabel ?? ''}
                onChange={(event) =>
                  updateStat(selectedStatKey, {
                    customLabel: event.target.value.trim() ? event.target.value : undefined,
                  })
                }
              />
            </label>
            {optionalNumberField(
              'Метка px',
              activeStat.labelFontSize,
              prefs.labelFontSize,
              (value) => updateStat(selectedStatKey, { labelFontSize: value }),
            )}
            {optionalNumberField(
              'Знач. px',
              activeStat.valueFontSize,
              prefs.valueFontSize,
              (value) => updateStat(selectedStatKey, { valueFontSize: value }),
            )}
            {optionalColorField('Цвет метки', activeStat.labelColor, prefs.labelColor, (value) =>
              updateStat(selectedStatKey, { labelColor: value }),
            )}
            {optionalColorField('Цвет знач.', activeStat.valueColor, prefs.valueColor, (value) =>
              updateStat(selectedStatKey, { valueColor: value }),
            )}
            {backgroundField(activeStat.background, resolvedCellBackground, (background) =>
              updateStat(selectedStatKey, { background }),
            )}
          </div>
          <div className="portable-ultra-panel__actions">
            <button
              type="button"
              className="portable-ultra-chip"
              onClick={() => clearStat(selectedStatKey)}
            >
              Сброс ячейки
            </button>
          </div>
        </div>
      ) : editRows ? (
        <p className="portable-ultra-huds__empty">
          Кликни по ячейке сетки, чтобы настроить её стиль
        </p>
      ) : null}

      <div className="portable-ultra-panel__actions">
        <button
          type="button"
          className={classNames('portable-ultra-chip', editRows && 'portable-ultra-chip--active')}
          onClick={() => onEditRowsChange(!editRows)}
        >
          Редакт. строки
        </button>
        <button
          type="button"
          className="portable-ultra-chip"
          onClick={() => {
            const resetPrefs = {
              ...DEFAULT_ULTRA_PREFS,
              headColors: { ...DEFAULT_ULTRA_HEAD_COLORS },
              columnOverrides: [],
              statOverrides: {},
              customStats: [],
              deletedLabels: [],
              gridSlots: DEFAULT_ULTRA_PREFS.gridSlots.slice(),
            };
            onChange(resetPrefs);
            writeUltraPrefs(resetPrefs);
          }}
        >
          Сброс
        </button>
      </div>
    </section>
  );
};

export default V2UltraStylePanel;
